import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Countdown from "@/components/Countdown";
import ShareTripDialog from "@/components/ShareTripDialog";
import { formatMoney, formatDate, formatShortDate } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import {
  LogOut,
  MapPin,
  CalendarDays,
  Wallet,
  Plane,
  Clock,
  Loader2,
  ArrowDownCircle,
  ArrowUpCircle,
  Sparkles,
  FileText,
  Download,
  Ticket,
  BookUser,
  BedDouble,
  ShieldCheck,
  Receipt,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  Passport: { icon: BookUser, label: "Passports" },
  Ticket: { icon: Ticket, label: "Tickets" },
  Voucher: { icon: Receipt, label: "Vouchers" },
  Hotel: { icon: BedDouble, label: "Hotels" },
  Insurance: { icon: ShieldCheck, label: "Insurance" },
  Other: { icon: FileText, label: "Other" },
};
const CATEGORY_ORDER = ["Passport", "Ticket", "Voucher", "Hotel", "Insurance", "Other"];

const FALLBACK_HERO = "https://images.unsplash.com/photo-1608904872226-19d69391c761?q=85&w=2000";

const Section = ({ children, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, ease: "easeOut" }}
    className={className}
  >
    {children}
  </motion.div>
);

function TripView({ trip }) {
  const openDoc = async (id) => {
    try {
      const { data } = await api.get(`/itineraries/${trip.id}/documents/${id}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch (err) {
      toast.error("Could not open document");
    }
  };

  const coverSrc = trip.cover_storage_path
    ? `${BACKEND_URL}/api/itineraries/${trip.id}/cover`
    : trip.cover_image || FALLBACK_HERO;

  const groupedDocs = (trip.documents || []).reduce((acc, d) => {
    const k = CATEGORY_META[d.category] ? d.category : "Other";
    (acc[k] = acc[k] || []).push(d);
    return acc;
  }, {});

  const paid = (trip.payments || [])
    .filter((p) => p.type === "payment")
    .reduce((s, p) => s + Number(p.amount || 0), 0);
  const total = Number(trip.total_amount || 0);
  const remaining = Number(trip.remaining_balance || 0);
  const progress = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : (remaining <= 0 ? 100 : 0);

  return (
    <div className="mb-28">
      {/* HERO */}
      <div className="relative h-[85vh] min-h-[560px] w-full overflow-hidden" data-testid="portal-hero">
        <img
          src={coverSrc}
          alt={trip.destination}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-background" />
        <div className="absolute top-20 right-6 z-20">
          <ShareTripDialog trip={trip} />
        </div>
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-primary text-xs sm:text-sm tracking-[0.35em] uppercase mb-4 flex items-center justify-center gap-2">
              <Plane className="w-4 h-4" /> {trip.destination || "Your Trip"}
            </p>
            <h1 className="font-serif text-4xl sm:text-6xl md:text-7xl font-light tracking-tighter text-white mb-3">
              {trip.title}
            </h1>
            {(trip.start_date || trip.end_date) && (
              <p className="text-muted-foreground tracking-wide mb-12">
                {formatShortDate(trip.start_date)}
                {trip.end_date ? ` — ${formatShortDate(trip.end_date)}` : ""}
              </p>
            )}
            <div className="glass rounded-2xl px-6 sm:px-14 py-8 sm:py-10 inline-block">
              <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-muted-foreground mb-6">
                Countdown to departure
              </p>
              <Countdown startDate={trip.start_date} />
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 -mt-16 relative z-10">
        {/* BALANCE */}
        <Section className="glass rounded-2xl p-8 sm:p-10 mb-16" >
          <div className="flex items-center gap-2 mb-6">
            <Wallet className="w-5 h-5 text-primary" />
            <h2 className="text-xs tracking-[0.25em] uppercase text-muted-foreground">Trip Balance</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            <div>
              <p className="text-xs tracking-widest uppercase text-muted-foreground mb-2">Total</p>
              <p className="font-serif text-4xl font-light tracking-tighter text-white">
                {formatMoney(total, trip.currency)}
              </p>
            </div>
            <div>
              <p className="text-xs tracking-widest uppercase text-muted-foreground mb-2">Paid</p>
              <p className="font-serif text-4xl font-light tracking-tighter gold-text">
                {formatMoney(paid, trip.currency)}
              </p>
            </div>
            <div>
              <p className="text-xs tracking-widest uppercase text-muted-foreground mb-2">Remaining</p>
              <p
                data-testid="portal-remaining-balance"
                className={`font-serif text-4xl font-light tracking-tighter ${remaining <= 0 ? "text-emerald-400" : "text-white"}`}
              >
                {formatMoney(remaining, trip.currency)}
              </p>
            </div>
          </div>
          <div className="mt-8">
            <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#A17E1A] to-[#F3E5AB] transition-all duration-700"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-right">{progress}% settled</p>
          </div>

          {/* Payment history */}
          {(trip.payments || []).length > 0 && (
            <div className="mt-10" data-testid="portal-payment-history">
              <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground mb-4">Payment History</p>
              <div className="space-y-3">
                {[...trip.payments].reverse().map((p) => (
                  <div key={p.id} className="flex items-center justify-between border-b border-border pb-3">
                    <div className="flex items-center gap-3">
                      {p.type === "charge" ? (
                        <ArrowUpCircle className="w-4 h-4 text-rose-400/80" />
                      ) : (
                        <ArrowDownCircle className="w-4 h-4 text-emerald-400/80" />
                      )}
                      <div>
                        <p className="text-sm text-foreground">{p.note || (p.type === "charge" ? "Charge" : "Payment")}</p>
                        <p className="text-xs text-muted-foreground">{formatShortDate(p.date)}</p>
                      </div>
                    </div>
                    <span className={`text-sm tabular-nums ${p.type === "charge" ? "text-rose-400/90" : "text-emerald-400/90"}`}>
                      {p.type === "charge" ? "+" : "−"}{formatMoney(p.amount, trip.currency)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>

        {/* ITINERARY TIMELINE */}
        {(trip.days || []).length > 0 && (
          <div className="mb-10">
            <Section>
              <p className="text-primary text-xs tracking-[0.3em] uppercase mb-2">Day by day</p>
              <h2 className="font-serif text-4xl font-light tracking-tighter mb-12">Your Itinerary</h2>
            </Section>
            <div className="relative border-l border-border ml-3 sm:ml-4">
              {trip.days.map((day, idx) => (
                <Section key={idx} className="relative pl-8 sm:pl-12 pb-14">
                  <div className="absolute -left-[7px] top-1.5 w-3.5 h-3.5 rounded-full bg-primary gold-glow" />
                  <div className="glass rounded-xl p-6 sm:p-8" data-testid={`portal-day-${idx}`}>
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-1">
                      <span className="text-xs tracking-[0.2em] uppercase text-primary">Day {idx + 1}</span>
                      {day.date && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <CalendarDays className="w-3 h-3" /> {formatDate(day.date)}
                        </span>
                      )}
                    </div>
                    <h3 className="font-serif text-2xl sm:text-3xl font-light tracking-tighter text-white">
                      {day.title}
                    </h3>
                    {day.destination && (
                      <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
                        <MapPin className="w-3.5 h-3.5 text-primary" /> {day.destination}
                      </p>
                    )}
                    {(day.activities || []).length > 0 && (
                      <div className="mt-6 space-y-4">
                        {day.activities.map((a, ai) => (
                          <div key={ai} className="flex gap-4">
                            <div className="flex flex-col items-center pt-1">
                              <div className="text-primary/70 text-xs tabular-nums w-14 text-right">
                                {a.time || <Clock className="w-3.5 h-3.5 inline" />}
                              </div>
                            </div>
                            <div className="border-l border-border pl-4 flex-1">
                              <p className="text-foreground">{a.title}</p>
                              {a.notes && <p className="text-sm text-muted-foreground mt-0.5">{a.notes}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Section>
              ))}
            </div>
          </div>
        )}

        {(trip.documents || []).length > 0 && (
          <Section className="glass rounded-xl p-8 mb-16">
            <div className="flex items-center gap-2 mb-6">
              <FileText className="w-5 h-5 text-primary" />
              <h2 className="text-xs tracking-[0.25em] uppercase text-muted-foreground">Travel Documents</h2>
            </div>
            <div className="space-y-8" data-testid="portal-documents">
              {CATEGORY_ORDER.filter((c) => groupedDocs[c]?.length).map((cat) => {
                const Icon = CATEGORY_META[cat].icon;
                return (
                  <div key={cat} data-testid={`portal-doc-group-${cat}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="w-4 h-4 text-primary" />
                      <h3 className="text-sm text-foreground/90 tracking-wide">{CATEGORY_META[cat].label}</h3>
                      <span className="text-xs text-muted-foreground">({groupedDocs[cat].length})</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {groupedDocs[cat].map((doc) => (
                        <button
                          key={doc.id}
                          type="button"
                          onClick={() => openDoc(doc.id)}
                          data-testid={`portal-doc-${doc.id}`}
                          className="flex items-center gap-3 bg-secondary/40 border border-border rounded-lg px-4 py-3 text-left hover:border-primary/40 transition-colors"
                        >
                          <Icon className="w-5 h-5 text-primary shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground truncate">{doc.original_filename}</p>
                            <p className="text-xs text-muted-foreground">{doc.category}</p>
                          </div>
                          <Download className="w-4 h-4 text-muted-foreground" />
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {trip.notes && (
          <Section className="glass rounded-xl p-8 flex gap-4">
            <Sparkles className="w-5 h-5 text-primary shrink-0 mt-1" />
            <div>
              <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground mb-2">A note for you</p>
              <p className="text-foreground/90 leading-relaxed whitespace-pre-line">{trip.notes}</p>
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

export default function ClientPortal() {
  const { user, logout } = useAuth();
  const [trips, setTrips] = useState(null);

  useEffect(() => {
    api.get("/me/itineraries").then(({ data }) => setTrips(data)).catch(() => setTrips([]));
  }, []);

  return (
    <div className="min-h-screen bg-background relative z-10">
      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Travel Mor" className="w-10 h-10 object-contain" />
            <span className="font-serif text-xl tracking-tighter hidden sm:block">Travel Mor</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:block">{user?.name}</span>
            <Button
              data-testid="portal-logout-btn"
              variant="ghost"
              onClick={logout}
              className="text-muted-foreground hover:text-foreground"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </nav>

      <div className="pt-16">
        {trips === null ? (
          <div className="min-h-[70vh] flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
          </div>
        ) : trips.length === 0 ? (
          <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6" data-testid="portal-empty">
            <Plane className="w-10 h-10 text-primary mb-6" />
            <h2 className="font-serif text-4xl font-light tracking-tighter mb-3">No trips yet</h2>
            <p className="text-muted-foreground max-w-md">
              Your travel designer is crafting something special. Your itinerary will appear here soon.
            </p>
          </div>
        ) : (
          <div className="pt-4">
            {trips.map((t) => (
              <TripView key={t.id} trip={t} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
